import React, { useMemo, useState, useRef, useEffect, useCallback } from "react";
import {
    Box,
    TextField,
    IconButton,
    Chip,
    Paper,
    Typography,
    InputAdornment,
    Collapse,
    Tooltip,
    alpha,
    useTheme,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    ClickAwayListener,
} from "@mui/material";
import {
    Add,
    Delete,
    DragIndicator,
    Edit,
    Close,
    Description,
    ExpandMore,
    Check,
} from "@mui/icons-material";
import ValueMetaDialog from "./ValueMetaDialog";

type ValueMetaRow = {
    option_name: string;
    option_value: string;
    description: string | null;
};

interface OptionInputProps {
    name: string;
    values: string[];
    onNameChange: (newName: string) => void;
    onValuesChange: (newValues: string[]) => void;
    onDelete: () => void;
    onValueRename?: (oldValue: string, newValue: string) => void;
    onValueDelete?: (value: string) => void;
    productId?: number;
    valueMeta?: ValueMetaRow[];
    onSaveValueMeta?: (args: {
        productId: number;
        option_name: string;
        option_value: string;
        description: string | null;
        old_option_value?: string;
    }) => Promise<void> | void;
    defaultExpanded?: boolean;
}

const normalize = (s: string) => (s || "").trim().replace(/\s+/g, " ");

const sortValues = (arr: string[]) =>
    [...arr]
        .map(normalize)
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));

const OptionInput: React.FC<OptionInputProps> = ({
    name,
    values,
    onNameChange,
    onValuesChange,
    onDelete,
    onValueRename,
    onValueDelete,
    productId,
    valueMeta = [],
    onSaveValueMeta,
    defaultExpanded = false,
}) => {
    const theme = useTheme();
    const [newValue, setNewValue] = useState("");
    const [editingValue, setEditingValue] = useState<string | null>(null);
    const [editInputValue, setEditInputValue] = useState("");
    const [metaOpen, setMetaOpen] = useState(false);
    const [expanded, setExpanded] = useState(defaultExpanded);
    
    // ✅ FIX 1: Local state for the name input (fast typing)
    const [localName, setLocalName] = useState(name);
    
    const editInputRef = useRef<HTMLInputElement>(null);
    const nameInputRef = useRef<HTMLInputElement>(null);

    const optionNameOk = useMemo(() => localName.trim().length > 0, [localName]);

    // ✅ FIX 2: Sync local state when prop changes from outside
    useEffect(() => {
        setLocalName(name);
    }, [name]);

    // ✅ FIX 3: Debounced save to parent (only update on blur or after typing stops)
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout>>();

    const handleNameChange = useCallback((newValue: string) => {
        setLocalName(newValue); // Fast local update
        
        // Clear existing timer
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        
        // Only update parent after 500ms of no typing
        debounceTimerRef.current = setTimeout(() => {
            if (newValue !== name) {
                onNameChange(newValue);
            }
        }, 500);
    }, [name, onNameChange]);

    // ✅ FIX 4: Also update parent on blur (immediate save)
    const handleNameBlur = useCallback(() => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        
        if (localName !== name) {
            onNameChange(localName);
        }
    }, [localName, name, onNameChange]);

    // Cleanup timer on unmount
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    // Count values with descriptions
    const valuesWithDescriptions = useMemo(() => {
        if (!valueMeta.length || !values.length) return 0;

        const nName = normalize(localName).toLowerCase();
        const allowed = new Set(values.map(v => normalize(v).toLowerCase()));

        return valueMeta.filter(m =>
            normalize(m.option_name).toLowerCase() === nName &&
            !!m.description?.trim() &&
            allowed.has(normalize(m.option_value).toLowerCase())
        ).length;
    }, [valueMeta, localName, values]);

    // Focus edit input when editing starts
    useEffect(() => {
        if (editingValue !== null && editInputRef.current) {
            editInputRef.current.focus();
        }
    }, [editingValue]);

    const handleAddValue = () => {
        if (!optionNameOk) return;

        const v = normalize(newValue);
        if (!v) return;

        const exists = values.some(
            (x) => normalize(x).toLowerCase() === v.toLowerCase()
        );

        if (!exists) {
            onValuesChange(sortValues([...values, v]));
            // Auto-expand when adding first value
            if (values.length === 0 && !expanded) {
                setExpanded(true);
            }
        }

        setNewValue("");
    };

    const handleDeleteValue = async (value: string, e?: React.MouseEvent) => {
        e?.stopPropagation();
        onValueDelete?.(value);
        onValuesChange(values.filter((v) => v !== value));

        if (typeof productId === "number" && onSaveValueMeta) {
            await onSaveValueMeta({
                productId,
                option_name: localName,
                option_value: value,
                description: null,
            });
        }

        if (editingValue === value) {
            setEditingValue(null);
            setEditInputValue("");
        }
    };

    const handleStartEdit = (value: string, e: React.MouseEvent) => {
        e.stopPropagation();
        setEditingValue(value);
        setEditInputValue(value);
        // Auto-expand when editing
        if (!expanded) {
            setExpanded(true);
        }
    };

    const handleCancelEdit = () => {
        setEditingValue(null);
        setEditInputValue("");
    };

    const handleSaveEdit = async () => {
        if (editingValue === null) return;

        const oldValue = editingValue;
        const v = normalize(editInputValue);
        if (!v || v === oldValue) {
            handleCancelEdit();
            return;
        }

        const duplicate = values.some((x) => {
            if (x === oldValue) return false;
            return normalize(x).toLowerCase() === v.toLowerCase();
        });
        
        if (duplicate) {
            return;
        }

        onValueRename?.(oldValue, v);

        const updated = values.map(val => val === oldValue ? v : val);
        onValuesChange(sortValues(updated));
        
        if (typeof productId === "number" && onSaveValueMeta) {
            const existing = valueMeta.find(m =>
                normalize(m.option_name).toLowerCase() === normalize(localName).toLowerCase() &&
                normalize(m.option_value).toLowerCase() === normalize(oldValue).toLowerCase()
            );

            await onSaveValueMeta({
                productId,
                option_name: localName,
                option_value: v,
                description: existing?.description ?? null,
                old_option_value: oldValue,
            });
        }

        setEditingValue(null);
        setEditInputValue("");
    };

    const handleNewValueKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            e.preventDefault();
            handleAddValue();
        }
    };

    const handleEditKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            e.preventDefault();
            void handleSaveEdit();
        }
        if (e.key === "Escape") {
            e.preventDefault();
            handleCancelEdit();
        }
    };

    return (
        <Accordion
            expanded={expanded}
            onChange={(_, isExpanded) => setExpanded(isExpanded)}
            elevation={0}
            sx={{
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
                '&:before': { display: 'none' },
                transition: theme.transitions.create(['box-shadow', 'border-color']),
                '&:hover': {
                    borderColor: theme.palette.primary.main,
                    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.08)}`,
                },
            }}
        >
            {/* Accordion Summary - Always visible */}
            <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{
                    px: 3,
                    py: 1,
                    '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.02),
                    },
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                    <DragIndicator
                        sx={{
                            color: 'text.disabled',
                            cursor: 'grab',
                            '&:active': { cursor: 'grabbing' }
                        }}
                    />

                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography
                            variant="subtitle1"
                            sx={{
                                fontWeight: 600,
                                color: localName ? 'text.primary' : 'text.secondary',
                            }}
                        >
                            {localName || "New Option"}
                        </Typography>

                        {values.length > 0 && (
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <Tooltip title={`${values.length} values`}>
                                    <Chip
                                        label={values.length}
                                        size="small"
                                        sx={{
                                            height: 24,
                                            minWidth: 32,
                                            '& .MuiChip-label': { px: 1, fontSize: '0.75rem' },
                                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                                            color: 'primary.main',
                                            fontWeight: 600,
                                        }}
                                    />
                                </Tooltip>

                                {valuesWithDescriptions > 0 && (
                                    <Tooltip title={`${valuesWithDescriptions} with descriptions`}>
                                        <Chip
                                            label="📝"
                                            size="small"
                                            sx={{
                                                height: 24,
                                                minWidth: 32,
                                                '& .MuiChip-label': { px: 1 },
                                                bgcolor: alpha(theme.palette.success.main, 0.1),
                                                color: 'success.main',
                                            }}
                                        />
                                    </Tooltip>
                                )}
                            </Box>
                        )}
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1 }} onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="Manage Descriptions">
                            <span>
                                <IconButton
                                    size="small"
                                    disabled={!productId || !localName.trim() || values.length === 0}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setMetaOpen(true);
                                    }}
                                    sx={{
                                        color: 'text.secondary',
                                        '&:hover': {
                                            color: 'primary.main',
                                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                                        },
                                    }}
                                >
                                    <Description fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>

                        <Tooltip title="Delete option">
                            <IconButton
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDelete();
                                }}
                                size="small"
                                sx={{
                                    color: 'text.secondary',
                                    '&:hover': {
                                        color: 'error.main',
                                        bgcolor: alpha(theme.palette.error.main, 0.08),
                                    },
                                }}
                            >
                                <Delete fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>
            </AccordionSummary>

            {/* Accordion Details - Expandable content */}
            <AccordionDetails sx={{ p: 3, borderTop: `1px solid ${theme.palette.divider}` }}>
                {/* ✅ FIX 5: Option name field with local state */}
                <TextField
                    label="Option name"
                    placeholder="e.g., Color, Size, Material"
                    value={localName}
                    onChange={(e) => handleNameChange(e.target.value)}
                    onBlur={handleNameBlur}
                    fullWidth
                    size="medium"
                    sx={{ mb: 3 }}
                    InputProps={{
                        sx: {
                            fontWeight: 500,
                            bgcolor: alpha(theme.palette.background.default, 0.6),
                        },
                    }}
                />

                {/* Add value field */}
                <Box sx={{ mb: 3 }}>
                    <TextField
                        value={newValue}
                        onChange={(e) => setNewValue(e.target.value)}
                        placeholder={optionNameOk ? "Add a value (press Enter)" : "Enter an option name first"}
                        onKeyDown={handleNewValueKeyDown}
                        disabled={!optionNameOk}
                        fullWidth
                        size="small"
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                bgcolor: alpha(theme.palette.background.default, 0.6),
                            },
                        }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        color="primary"
                                        onClick={handleAddValue}
                                        disabled={!optionNameOk || !newValue.trim()}
                                        size="small"
                                        sx={{
                                            bgcolor: !optionNameOk || !newValue.trim() ? 'transparent' : alpha(theme.palette.primary.main, 0.08),
                                            '&:hover': {
                                                bgcolor: alpha(theme.palette.primary.main, 0.12),
                                            },
                                        }}
                                    >
                                        <Add />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>

                {/* Values chips with inline editing */}
                <Collapse in={values.length > 0}>
                    <Box sx={{ mb: 2 }}>
                        <Typography
                            variant="caption"
                            sx={{
                                display: 'block',
                                mb: 1.5,
                                color: 'text.secondary',
                                fontWeight: 500,
                                letterSpacing: 0.5,
                            }}
                        >
                            VALUES ({values.length})
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {values.map((value) => {
                                const isEditing = editingValue === value;
                                
                                if (isEditing) {
                                    return (
                                        <ClickAwayListener key={value} onClickAway={handleSaveEdit}>
                                            <Paper
                                                elevation={4}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    p: '2px',
                                                    bgcolor: 'background.paper',
                                                    border: `2px solid ${theme.palette.primary.main}`,
                                                    borderRadius: '16px',
                                                    minWidth: 120,
                                                }}
                                            >
                                                <TextField
                                                    inputRef={editInputRef}
                                                    value={editInputValue}
                                                    onChange={(e) => setEditInputValue(e.target.value)}
                                                    onKeyDown={handleEditKeyDown}
                                                    size="small"
                                                    variant="standard"
                                                    fullWidth
                                                    placeholder="Edit value"
                                                    InputProps={{
                                                        disableUnderline: true,
                                                        sx: {
                                                            px: 1.5,
                                                            py: 0.5,
                                                            fontSize: '0.875rem',
                                                        },
                                                    }}
                                                />
                                                <Box sx={{ display: 'flex', gap: 0.5, pr: 0.5 }}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={handleSaveEdit}
                                                        color="primary"
                                                        sx={{
                                                            p: 0.5,
                                                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                                                            '&:hover': {
                                                                bgcolor: alpha(theme.palette.primary.main, 0.2),
                                                            },
                                                        }}
                                                    >
                                                        <Check fontSize="small" />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        onClick={handleCancelEdit}
                                                        sx={{
                                                            p: 0.5,
                                                            color: 'text.secondary',
                                                            '&:hover': {
                                                                bgcolor: alpha(theme.palette.text.secondary, 0.08),
                                                            },
                                                        }}
                                                    >
                                                        <Close fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            </Paper>
                                        </ClickAwayListener>
                                    );
                                }
                                
                                return (
                                    <Chip
                                        key={value}
                                        label={value}
                                        onClick={(e) => handleStartEdit(value, e)}
                                        onDelete={(e) => handleDeleteValue(value, e)}
                                        color="primary"
                                        variant="outlined"
                                        deleteIcon={<Delete style={{ fontSize: 18 }} />}
                                        sx={{
                                            cursor: 'pointer',
                                            borderRadius: 1.5,
                                            transition: theme.transitions.create(['background-color', 'box-shadow']),
                                            '&:hover': {
                                                bgcolor: alpha(theme.palette.primary.main, 0.08),
                                                '& .edit-icon': {
                                                    opacity: 1,
                                                },
                                            },
                                        }}
                                        icon={
                                            <Edit 
                                                className="edit-icon" 
                                                style={{ 
                                                    fontSize: 16, 
                                                    opacity: 0,
                                                    transition: 'opacity 0.2s',
                                                    marginLeft: 4,
                                                }} 
                                            />
                                        }
                                    />
                                );
                            })}
                        </Box>
                    </Box>
                </Collapse>

                {/* Empty state hint */}
                {values.length === 0 && localName && (
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'text.secondary',
                            bgcolor: alpha(theme.palette.info.main, 0.04),
                            p: 1.5,
                            borderRadius: 2,
                            border: `1px dashed ${theme.palette.divider}`,
                            textAlign: 'center',
                        }}
                    >
                        Add at least one value for this option
                    </Typography>
                )}
            </AccordionDetails>

            {/* Meta dialog */}
            {typeof productId === "number" && onSaveValueMeta && (
                <ValueMetaDialog
                    open={metaOpen}
                    onClose={() => setMetaOpen(false)}
                    productId={productId}
                    optionName={localName}
                    values={values}
                    valueMeta={valueMeta}
                    onSaveValueMeta={async (args) => {
                        await onSaveValueMeta(args);
                    }}
                />
            )}
        </Accordion>
    );
};

// ✅ FIX 6: Wrap with React.memo to prevent unnecessary re-renders
export default React.memo(OptionInput);